import React, { createContext, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { user, token, updateUser } = useAuth();

  const isAdmin = user?.role?.toLowerCase() === 'admin';

  // Permissions come from user object (set during login / getMe)
  const permissions = user?.permissions || null;

  // Loading = user exists but permissions haven't arrived yet
  // Admin always has access so never loading for admin
  const loading = !!token && !user;

  const can = useCallback(
    (module, action = 'view') => {
      if (isAdmin) return true;
      if (!permissions) return false;
      const mod = permissions[module];
      if (!mod) return false;
      return !!mod[action];
    },
    [isAdmin, permissions]
  );

  const canView = useCallback((module) => can(module, 'view'), [can]);
  const canEdit = useCallback((module) => can(module, 'edit'), [can]);
  const canCreate = useCallback((module) => can(module, 'create'), [can]);
  const canDelete = useCallback((module) => can(module, 'delete'), [can]);
  const canAction = useCallback((module) => can(module, 'action'), [can]);

  // Refresh permissions from backend (after admin saves changes)
  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/permissions/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        updateUser({ permissions: data.permissions || {} });
      }
    } catch (e) {
      console.error('Failed to refresh permissions:', e);
    }
  }, [token, updateUser]);

  // iter287/iter289 — Auto-refresh /api/permissions/me on mount AND whenever
  // the auth token actually changes (login / re-login). Guarded by a ref that
  // tracks the last token we already refreshed for so we do NOT re-run when
  // the `refresh` closure identity flips (that was causing an infinite login
  // render loop).
  const lastRefreshedToken = useRef(null);
  useEffect(() => {
    if (token && lastRefreshedToken.current !== token) {
      lastRefreshedToken.current = token;
      refresh();
    } else if (!token) {
      lastRefreshedToken.current = null;
    }
  }, [token, refresh]);

  const value = useMemo(
    () => ({ permissions, loading, can, canView, canEdit, canCreate, canDelete, canAction, isAdmin, refresh }),
    [permissions, loading, can, canView, canEdit, canCreate, canDelete, canAction, isAdmin, refresh]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissions must be inside PermissionProvider');
  return ctx;
};
